import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
} from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { elevation, mdSysColor, radius, tap, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/**
 * FSE BottomNav — MD3 bottom navigation bar.
 *
 * Spec: `Documentation/Claude design files/preview/bottom-nav.html`. Per MD3:
 * surfaceContainer background, level-1 shadow, ≥64dp tall (rural-bumped to the
 * 80dp expressive variant), each item a stacked icon + label. The active item
 * shows a 64×32dp pill (leaf `secondaryContainer`) behind its icon, and the
 * active icon switches to the filled Material Symbols axis.
 *
 * Wraps MUI `BottomNavigation`. Owns the public prop surface — consumers never
 * touch MUI's `sx`. Exposed through @fse/ui so consumers never import
 * @mui/material directly (swap-promise).
 */
export interface BottomNavItem {
  /** Stable identifier reported through `onChange`. */
  value: string;
  /** Visible label under the icon. */
  label: string;
  /** Material Symbols ligature name (e.g. "storefront"). */
  icon: string;
}

export interface BottomNavProps {
  /** Navigation destinations, rendered left-to-right. */
  items: BottomNavItem[];
  /** Currently selected item `value`. */
  value: string;
  /** Fired with the newly selected item `value`. */
  onChange: (value: string) => void;
}

export const BottomNav = forwardRef<HTMLDivElement, BottomNavProps>(function BottomNav(
  { items, value, onChange },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <BottomNavigation
      ref={ref}
      value={value}
      onChange={(_event, next: string) => onChange(next)}
      showLabels
      sx={{
        height: 80,
        minHeight: tap.lg,
        pt: 1.5,
        pb: 1,
        // MD3 surfaceContainer tier (tonal hierarchy, not an elevation overlay)
        backgroundColor: sys.surfaceContainer,
        boxShadow: elevation.level1,
        '& .MuiBottomNavigationAction-root': {
          minWidth: 0,
          gap: '4px',
          color: sys.onSurfaceVariant,
        },
        '& .MuiBottomNavigationAction-label': {
          fontFamily: typography.fontFamily,
          fontSize: `${typography.labelSmall.fontSize}px`,
          fontWeight: 600,
          color: sys.onSurfaceVariant,
        },
        '& .Mui-selected .MuiBottomNavigationAction-label': {
          fontSize: `${typography.labelSmall.fontSize}px`,
          fontWeight: 700,
          color: sys.onSurface,
        },
      }}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <BottomNavigationAction
            key={item.value}
            value={item.value}
            label={item.label}
            disableRipple
            icon={
              <Box
                sx={{
                  width: 64,
                  height: 32,
                  borderRadius: `${radius.full}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 200ms cubic-bezier(0.2,0,0,1)',
                  backgroundColor: selected ? sys.secondaryContainer : 'transparent',
                }}
              >
                <Icon
                  name={item.icon}
                  size={24}
                  fill={selected}
                  color={selected ? sys.onSecondaryContainer : sys.onSurfaceVariant}
                />
              </Box>
            }
          />
        );
      })}
    </BottomNavigation>
  );
});
